import * as allValidators from 'vuelidate/lib/validators'
import * as http from './http'

import i18next from 'i18next'
import { decode } from 'jsonwebtoken'
import { flatten } from 'lodash'
import moment from 'moment'
import uuid from 'uuid/v4'

export const getValidationErrors = messages => {
  return field => {
    if (!field || !field.$dirty) return []

    let keys = Object.keys(field)
    const errorKeys = keys.filter(x => !field[x] && x in messages)

    return errorKeys.map(ek => messages[ek])
  }
}

export const submitGuard = async (instance, submitFunction, handleError) => {
  instance.$v.$touch()
  if (instance.$v.$invalid || instance.submitInProgress) {
    if (handleError) return handleError(instance)

    return console.warn('Form not valid')
  }
  instance.submitInProgress = true
  try {
    await submitFunction()
  } finally {
    instance.submitInProgress = false
  }
}

export const defaultMessages = {
  required: 'This field is required',
  date: 'This field must be date',
  between: 'This field is out of range',
  minLength: 'This field should contain more values',
  maxLength: 'This field should contain less values',
  minDate: 'This field should be after',
  maxDate: 'This field should be before',
  email: 'This field must be an email',
  httpUrl: 'This field must be http or https url',
  minValue: 'Provided value is too small',
  maxValue: 'Provided value is too big',
  sameAs: 'This field must be the same as some other field',
  notIn: 'Provided value is already taken',
  numeric: 'This field must be a number',
  url: 'This field must a valid url',
  uniqueTicketCategoryPriority: 'Category priority is already in use',
  sameAsEmail: 'Email addresses do not match. Please ensure both email fields are identical',
  minValueDynamic: 'Value cannot be less than minimum required',
}

export const tvornicaKultureCustomOrganizerMsg =
  'UPOZORENJE POSJETITELJIMA!\n' +
  'PUŠENJE u Velikom pogonu Tvornice kulture je NAJSTROŽE ZABRANJENO!\n' +
  'Posjetitelji koji neće poštovati zabranu pušenja biti će udaljeni iz dvorane!'

export const hexToRGB = hex => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const longHex = hex.replace(shorthandRegex, (m, r, g, b) => {
    return r + r + g + g + b + b
  })

  const colors = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(longHex)

  return colors
    ? {
        r: parseInt(colors[1], 16),
        g: parseInt(colors[2], 16),
        b: parseInt(colors[3], 16),
      }
    : null
}

export const rgbaTorgb = RGBA => {
  const RGB = {}
  const bg = { r: 255, g: 255, b: 255 }
  const alpha = 1 - RGBA.a
  RGB.r = Math.round((RGBA.a * (RGBA.r / 255) + alpha * (bg.r / 255)) * 255)
  RGB.g = Math.round((RGBA.a * (RGBA.g / 255) + alpha * (bg.g / 255)) * 255)
  RGB.b = Math.round((RGBA.a * (RGBA.b / 255) + alpha * (bg.b / 255)) * 255)

  return RGB
}

export const calculateLuminance = (r, g, b) => {
  // for more info about the luminance constants
  // https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-procedure
  const a = [r, g, b].map(function (v) {
    v /= 255

    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
}

export const calculateContrast = (rgb1, rgb2) => {
  const lum1 = calculateLuminance(rgb1[0], rgb1[1], rgb1[2])
  const lum2 = calculateLuminance(rgb2[0], rgb2[1], rgb2[2])
  const brightest = Math.max(lum1, lum2)
  const darkest = Math.min(lum1, lum2)

  return (brightest + 0.05) / (darkest + 0.05)
}

export const WCGAcheck = (chosenColor, colors, opacity) => {
  const { r, g, b } = chosenColor
  const defaultOpacity = 0.05
  const RGBA = { ...chosenColor }
  RGBA.a = opacity || defaultOpacity
  const RGB = rgbaTorgb(RGBA)
  if (calculateContrast([r, g, b], [RGB.r, RGB.g, RGB.b]) < 4.5) {
    chosenColor = { ...colors.primary }

    return chosenColor
  }

  return chosenColor
}

export const validateWorkshops = (selectedWorkshops, workshops) => {
  return flatten(
    selectedWorkshops.map(selectedWorkshop => {
      const from = moment(selectedWorkshop.from)
      const to = moment(selectedWorkshop.to)

      return workshops.filter(w => {
        const wFrom = moment(w.from)
        const wTo = moment(w.to)

        return from.isBefore(wTo) && to.isAfter(wFrom)
      })
    }),
  )
}

export const findCountry = (worldCountries, parsedCountry) => {
  return worldCountries.find(({ official, common, ioc, iso2, iso3, fifa, other }) => {
    const officialMatch = official && official.toLowerCase().trim() === parsedCountry
    const commonMatch = common && common.toLowerCase().trim() === parsedCountry
    const iocMatch = ioc && ioc.toLowerCase().trim() === parsedCountry
    const iso2Match = iso2 && iso2.toLowerCase().trim() === parsedCountry
    const iso3Match = iso3 && iso3.toLowerCase().trim() === parsedCountry
    const fifaMatch = fifa && fifa.toLowerCase().trim() === parsedCountry
    const otherMatch = other && other.map(o => o.toLowerCase().trim()).includes(parsedCountry)

    return (
      officialMatch || commonMatch || iocMatch || iso2Match || iso3Match || fifaMatch || otherMatch
    )
  })
}

export const toCSS = object => {
  const styles = {}
  if (object.size) {
    styles['font-size'] = `${object.size}px`
  }
  if (object.font) {
    styles['font-family'] = object.font
  }
  if (object.color) {
    styles['color'] = object.color
  }
  if (object.textAlign) {
    styles['text-align'] = object.textAlign
  }
  if (object.backgroundColor) {
    styles['background-color'] = object.backgroundColor
  }
  if (object.bold) {
    styles['font-weight'] = 'bold'
  }
  if (object.italic) {
    styles['font-style'] = 'italic'
  }
  if (object.underline) {
    styles['text-decoration'] = 'underline'
  }
  if (object.uppercase) {
    styles['text-transform'] = 'uppercase'
  }
  if (object.backgroundSize) {
    styles['background-size'] = object.backgroundSize
  }
  if (object.backgroundImage) {
    styles['background-image'] = `url(${object.backgroundImage})`
  }
  if (object.borderRadius) {
    styles['border-radius'] = `${object.borderRadius}px`
  }
  if (object.z) {
    styles['z-index'] = object.z
  }
  if (object.x) {
    styles['left'] = `${object.x}px`
  }
  if (object.y) {
    styles['top'] = `${object.y}px`
  }
  if (object.w) {
    styles['width'] = `${object.w}px`
  }
  if (object.h) {
    styles['height'] = `${object.h}px`
  }
  if (object.rotate) {
    styles['transform'] = `rotate(${object.rotate}deg)`
  }
  if (object.position) {
    styles['position'] = object.position
  }
  if (object.clip) {
    styles['clip'] = object.clip
  }
  if (object.top) {
    styles['top'] = `${object.top}px`
  }

  return styles
}

export const parseDate = (date, format = 'DD.MM.YYYY') => {
  return moment(date).format(format)
}

export const dataURIToFile = (uri, name, type) => {
  const binStr = atob(uri.split(',')[1])
  let arr = new Uint8Array(binStr.length)
  for (let i = 0; i < binStr.length; i++) {
    arr[i] = binStr.charCodeAt(i)
  }
  // File constructor does not exist in Edge

  return new File([arr], name, { type })
}

export const serverURL = process.env.VUE_APP_SERVER_URL
export const frontendURL = process.env.VUE_APP_FRONTEND_URL
export const customerURL = process.env.VUE_APP_CUSTOMER_URL
export const landingURL = process.env.VUE_APP_LANDING_URL
export const zkmURL = process.env.VUE_APP_ZKM_URL

export const dataURLToImage = url => {
  return new Promise(resolved => {
    const image = new Image()
    image.onload = function () {
      resolved(this)
    }
    image.crossOrigin = 'Anonymous'
    image.src = url
  })
}

export const resizeWidthAndHeight = (width, height, MAX_WIDTH = 300, MAX_HEIGHT = 300) => {
  if (width > height) {
    if (width > MAX_WIDTH) {
      return {
        height: (height * MAX_WIDTH) / width,
        width: MAX_WIDTH,
      }
    }
  } else {
    if (height > MAX_HEIGHT) {
      return {
        width: (width * MAX_HEIGHT) / height,
        height: MAX_HEIGHT,
      }
    }
  }

  return { width, height }
}

export const resizeImage = async (image, options) => {
  const { width, height } = resizeWidthAndHeight(
    image.width,
    image.height,
    options.width,
    options.height,
  )
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  context.drawImage(image, 0, 0, width, height)
  const toFile = options && options.to === 'file'

  return new Promise(resolve => {
    if (toFile) {
      canvas.toBlob(resolve, options.mime || 'image/jpeg', 1.0)
    } else {
      const dataURL = canvas.toDataURL(options.mime || 'image/jpeg')

      return resolve(dataURLToImage(dataURL))
    }
  })
}

export const fileToImage = async file => {
  if (!(file instanceof Blob)) {
    return Promise.reject({ error: 'Invalid file format' })
  }

  return new Promise(resolve => {
    const fr = new FileReader()
    fr.onload = () => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.src = fr.result.toString()
    }
    fr.readAsDataURL(file)
  })
}

export const fileToDataUrl = async file => {
  if (!(file instanceof Blob)) {
    return Promise.reject({ error: 'Invalid file format' })
  }

  return new Promise(resolve => {
    const fr = new FileReader()
    fr.onload = () => {
      resolve(fr.result)
    }
    fr.readAsDataURL(file)
  })
}

export const urlToDataURL = async url => {
  const image = await dataURLToImage(url)

  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height
  const context = canvas.getContext('2d')
  context.drawImage(image, 0, 0, image.width, image.height)

  return canvas.toDataURL()
}

export const uploadImageFromDataUrl = async (dataURL, path) => {
  const image = await dataURLToImage(dataURL)
  const file = await resizeImage(image, { to: 'file' })
  const { upload: uploadURL, download: downloadURL } = await http.getSignedURL(path)
  await http.uploadImage(uploadURL, file)

  return downloadURL
}

export const uploadImage = async (file, path) => {
  const { upload: uploadURL, download: downloadURL } = await http.getSignedURL(path)
  await http.uploadImage(uploadURL, file)

  return downloadURL
}

export function getUserFromToken(token) {
  return decode(token)
}

export function strToBool(str) {
  return str ? str.toLowerCase().trim() === 'true' : undefined
}

export const isSafari = () => {
  return (
    navigator.vendor &&
    navigator.vendor.indexOf('Apple') > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf('CriOS') == -1 &&
    navigator.userAgent.indexOf('FxiOS') == -1
  )
}

export const getMimeFromDataURL = dataURL => {
  return dataURL.substring('data:image/'.length, dataURL.indexOf(';base64'))
}

export const isDataURL = string => {
  return string.startsWith('data:image/')
}

export const getUploadOptions = async (data, path, options = {}) => {
  if (options.resize === false) {
    return { file: data, path: `${path}/${uuid()}` }
  }
  const mime = getMimeFromDataURL(data)
  const image = await dataURLToImage(data)
  const { width, height } = options
  const file = await resizeImage(image, { to: 'file', mime, width, height })

  return { file, path: `${path}/${uuid()}` }
}

export async function validateEventRoutingRules(rules, event, userType) {
  if (!rules || rules.length === 0) {
    return true
  }
  for (const rule of rules) {
    if (!(await rule(event, userType))) return false
  }

  return true
}

export async function validateRoutingRules(rules) {
  const user = await http.getUser()
  if (!user) return false

  for (const rule of rules) {
    if (!(await rule(user))) return false
  }

  return true
}

export const formatDate = (date, format) => moment(date).format(format || 'LL, H:mm')

export const getTimeUntilExpiresAsString = expirationDate => {
  const now = moment()
  const expires = moment(expirationDate)
  const duration = moment.duration(expires.diff(now))

  const years = duration.years()
  const months = duration.months()
  const days = duration.days()
  const hours = duration.hours()
  const minutes = duration.minutes()

  const parts = []
  if (years > 0) parts.push(`${years}y`)
  if (months > 0) parts.push(`${months}m`)
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}min`)

  return parts.length > 0 ? parts.join(' ') : 'Expired'
}

export const createAttendeeSchema = (definition, values = {}) => {
  const validationSchema = {}
  const attendeeFields = []
  for (const [key, field] of Object.entries(definition)) {
    const validators = {}
    const errorMessages = {
      required: i18next.t('required'),
      date: i18next.t('date'),
      between: i18next.t('between'),
      minLength: i18next.t('minLength'),
      maxLength: i18next.t('maxLength'),
      minDate: i18next.t('minDate'),
      maxDate: i18next.t('maxDate'),
      email: i18next.t('Email'),
      httpUrl: i18next.t('httpUrl'),
      minValue: i18next.t('minValue'),
      maxValue: i18next.t('maxValue'),
      sameAs: i18next.t('sameAs'),
      notIn: i18next.t('notIn'),
      numeric: i18next.t('numeric'),
      url: i18next.t('url'),
    }
    Object.entries(field.validations || []).forEach(([key, value]) => {
      if (value) {
        const isRequiredSwitchField = key === 'required' && field.component === 'switch-field'
        if (isRequiredSwitchField) {
          validators['sameAs'] = allValidators['sameAs'](() => true)
          errorMessages['sameAs'] = 'This field is required'
        } else if (allValidators[key]) {
          validators[key] = allValidators[key]
        }
      }
    })
    let label = key
    if (key === 'First name') label = i18next.t('firstName')
    if (key === 'Last name') label = i18next.t('lastName')
    if (key === 'postalCode') label = i18next.t('postalCode')
    if (key === 'City') label = i18next.t('cityOptional')
    if (key === 'Country') label = i18next.t('countryOptional')
    if (['Email', 'Address'].includes(key)) label = i18next.t(key.toLowerCase())

    attendeeFields.push({
      key,
      value: values[key],
      ...field,
      label,
      errorMessages,
    })
    validationSchema[key] = validators
  }

  return {
    validationSchema,
    attendeeFields: attendeeFields.sort((a, b) => a.order - b.order),
  }
}

export const objectToArray = (object, field) => {
  return Object.entries(object).map(([key, value]) => {
    return field
      ? {
          [field]: key,
          ...value,
        }
      : value
  })
}

export const findMaxReducer = mapper => (acc, item) => {
  if (mapper) return mapper(item) > acc ? mapper(item) : acc

  return item > acc ? item : acc
}

export const getArray = object => {
  if (Array.isArray(object)) return object

  return [object]
}
export const isDateBetween = val => {
  const first = Math.floor(moment(new Date()).diff(new Date(val.from), 'days', true)) >= 0
  const last = Math.ceil(moment(new Date()).diff(new Date(val.to), 'days', true)) <= 0

  return first && last
}

export const getLecturerNames = lecturers => {
  return (lecturers || [])
    .map(({ fields }) => `${fields['First name']} ${fields['Last name']}`)
    .join(', ')
}

export const setVisibleFields = (visibleFields, eventId) => {
  const allFields = JSON.parse(localStorage.getItem('visibleFields')) || {}

  localStorage.setItem(
    'visibleFields',
    JSON.stringify({ ...allFields, [eventId]: visibleFields.slice(0, 10) }),
  )
}

export const getVisibleFields = eventId => {
  const allFields = JSON.parse(localStorage.getItem('visibleFields')) || {}
  const visibleFields = allFields[eventId]

  return visibleFields || []
}

export async function initializeRegisterForm(order) {
  let form = { attendee: { fields: {} } }
  let shoppingCart = order.purchases.map(p => ({ ...p.orderItem }))

  return [form, shoppingCart]
}

export async function createPurchaseSubmitForm(
  form,
  totalPrice,
  order,
  shoppingCart,
  eventId = '',
) {
  const storeId = process.env.VUE_APP_CORVUS_STORE_ID
  const corvusURL = process.env.VUE_APP_CORVUS_URL
  const currency = totalPrice.toString().substring(0, 3)
  const price = parseFloat(totalPrice.match(/\d*\.?\d+/))
  let result = Object.values(
    shoppingCart.reduce((r, e) => {
      let k = `${e.id}`
      if (!r[k]) r[k] = { ...e, count: 1 }
      else r[k].count += 1

      return r
    }, {}),
  )
  const description = result
    .map(function (orderItem) {
      return `${orderItem.count}x${eventId}-${orderItem.name.trim()}`
    })
    .join(', ')
  const purchase = {
    amount: price,
    cardholder_email: form.attendee.email ? form.attendee.email : '',
    cardholder_name: form.attendee.firstName,
    cardholder_surname: form.attendee.lastName,
    cardholder_address: form.attendee.street ? form.attendee.street : '',
    cardholder_country: form.attendee.country ? form.attendee.country : '',
    cardholder_city: form.attendee.city ? form.attendee.city : '',
    cardholder_zip_code: form.attendee.postalCode ? form.attendee.postalCode : '',
    cart: description,
    currency: currency,
    language: 'hr',
    order_number: order.id,
    require_complete: 'false',
    store_id: storeId,
    version: '1.3',
  }
  const purchaseString = Object.keys(purchase)
    .sort()
    .reduce((acc, key) => {
      return acc + key + purchase[key]
    }, '')

  purchase['signature'] = await http.getPurchaseSignature(purchaseString, order.eventId)

  const submitForm = document.createElement('form')
  submitForm.method = 'POST'
  submitForm.action = corvusURL

  Object.entries(purchase).map(([key, value]) => {
    const customInput = document.createElement('input')
    customInput.type = 'hidden'
    customInput.name = key
    customInput.value = value
    submitForm.appendChild(customInput)
  })

  return submitForm
}

export async function createPurchaseSubmitFormWSPay(
  form,
  totalPrice,
  order,
  lang = 'HR',
  shoppingCart,
  eventId = '',
) {
  const tickets = shoppingCart.reduce((prev, curr) => {
    const id = curr.id

    return {
      ...prev,
      [id]: (prev[id] || 0) + 1,
    }
  }, {})

  const shoppingCartId =
    Object.entries(tickets)
      .sort((a, b) => a[0] - b[0])
      .map(([ticketId, ticketCount]) => {
        return `${ticketId}x${ticketCount}`
      })
      .join('.') + `.ox${order.id}.ex${eventId}`

  let price = parseFloat(totalPrice.match(/\d*\.?\d+/))
  const formData = {
    ShopID: process.env.VUE_APP_WS_PAY_SHOP_ID,
    ShoppingCartID: shoppingCartId,
    Version: '2.0',
    TotalAmount: price.toFixed(2).replace('.', ','),
    Signature: '',
    Lang: lang.toUpperCase(),
    ReturnURL: `${serverURL}/ws-pay/successful-payment`,
    ReturnErrorURL: `${serverURL}/ws-pay/error-payment`,
    CancelURL: `${serverURL}/ws-pay/canceled-payment`,
    CustomerFirstName: form.attendee.firstName || form.attendee['First name'],
    CustomerLastName: form.attendee.lastName || form.attendee['Last name'],
    CustomerAddress: form.attendee.Address ? form.attendee.Address : '',
    CustomerCity: form.attendee.City ? form.attendee.City : '',
    CustomerZIP: form.attendee['Postal code'] ? form.attendee['Postal code'] : '',
    CustomerPhone: form.attendee.mobile ? form.attendee.mobile : '',
    CustomerCountry: form.attendee.Country ? form.attendee.Country : '',
    CustomerEmail: form.attendee.email || form.attendee['Email'] || '',
    PaymentMethod: form.paymentMethod,
    ...(Object.keys(tickets).some(t => [8591, 8593, 11841, 11846, 11847].includes(Number(t)))
      ? { CreditCardName: 'MASTERCARD' }
      : {}),
  }
  formData.Signature = await http.getWSPurchaseSignature(
    shoppingCartId,
    Math.round(price * 100).toString(),
  )

  const submitForm = document.createElement('form')
  submitForm.method = 'POST'
  submitForm.action = `${process.env.VUE_APP_WS_PAY_URL}/authorization.aspx`
  Object.entries(formData).map(([key, value]) => {
    const customInput = document.createElement('input')
    customInput.type = 'hidden'
    customInput.name = key
    customInput.value = value
    submitForm.appendChild(customInput)
  })
  document.body.appendChild(submitForm)

  return submitForm
}

export async function createCustomPurchaseSubmitFormWSPay(
  form,
  totalPrice,
  order,
  shoppingCart,
  eventId = '',
) {
  const tickets = shoppingCart.reduce((prev, curr) => {
    const id = curr.id

    return {
      ...prev,
      [id]: (prev[id] || 0) + 1,
    }
  }, {})

  const countPerTicket = {
    ...tickets,
    o: order.id,
    e: eventId,
  }

  const shoppingCartId = Object.entries(countPerTicket)
    .map(([ticketId, ticketCount]) => {
      return `${ticketId}x${ticketCount}`
    })
    .join('.')

  let price = parseFloat(totalPrice.match(/\d*\.?\d+/))

  const formData = {
    ShopID: process.env.VUE_APP_WS_PAY_SHOP_ID,
    ShoppingCartID: shoppingCartId,
    Version: '2.0',
    TotalAmount: price.toFixed(2).replace('.', ','),
    Signature: '',
    ReturnURL: `${serverURL}/ws-pay/custom-successful-payment`,
    ReturnErrorURL: `${serverURL}/ws-pay/error-payment`,
    CancelURL: `${serverURL}/ws-pay/canceled-payment`,
    CustomerFirstName: form.attendee.firstName,
    CustomerLastName: form.attendee.lastName,
    CustomerAddress: form.attendee.street ? form.attendee.street : '',
    CustomerCity: form.attendee.city ? form.attendee.city : '',
    CustomerZIP: form.attendee.postalCode ? form.attendee.postalCode : '',
    CustomerCountry: form.attendee.country ? form.attendee.country : '',
    CustomerEmail: form.attendee.email ? form.attendee.email : '',
    PaymentMethod: form.paymentMethod,
  }
  formData.Signature = await http.getWSPurchaseSignature(
    shoppingCartId,
    Math.round(price * 100).toString(),
  )

  const submitForm = document.createElement('form')
  submitForm.method = 'POST'
  submitForm.action = `${process.env.VUE_APP_WS_PAY_URL}/authorization.aspx`
  Object.entries(formData).map(([key, value]) => {
    const customInput = document.createElement('input')
    customInput.type = 'hidden'
    customInput.name = key
    customInput.value = value
    submitForm.appendChild(customInput)
  })
  document.body.appendChild(submitForm)

  return submitForm
}

export const exchangeRate = 7.5345

export const heatmapColorsArray = ['#E6F1F8', '#CEE4F2', '#56A4EF', '#0277BD', '#004C8C']

export const tisakTicketSeller = 'tisak'

export const croatianMonths = [
  'Siječanj',
  'Veljača',
  'Ožujak',
  'Travanj',
  'Svibanj',
  'Lipanj',
  'Srpanj',
  'Kolovoz',
  'Rujan',
  'Listopad',
  'Studeni',
  'Prosinac',
]

export const convertUtcToCroatianLocalTime = (dateTime, format) => {
  return moment.utc(dateTime, format).tz('Europe/Zagreb').format(format)
}

export const chartBackgroundColors = [
  '#0277BD',
  '#CEE4F2',
  '#6B02BD',
  '#B902BD',
  '#BD0202',
  '#BD5C02',
  '#988A14',
  '#0E7E1A',
  '#0F8890',
]

export const isHNBOrganization = id => {
  return [271, 129, 165].includes(id)
}

export const showPromoCodeInputInRegisterContainer = (eventId, organizationId) => {
  // add event id in array to remove promo code input
  const eventIdsWithoutPromoCodeInput = [1744, 1745, 2787, 3377, 2247, 2444, 4531]
  const shouldEventHavePromoCodeInput = !eventIdsWithoutPromoCodeInput.includes(eventId)

  return shouldEventHavePromoCodeInput && !isHNBOrganization(organizationId)
}

const inAppBrowsers = [
  'FBAN',
  'FBAV',
  'Twitter',
  'Instagram',
  'TikTok',
  'FBAN/Messenger',
  'FB_IAB/MESSENGER',
]

export const isInAppBrowser = userAgent => {
  return inAppBrowsers.some(item => userAgent.includes(item))
}

export const getIOSVersion = userAgent => {
  const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/)
  if (match && match.length > 1) {
    return parseInt(match[1], 10)
  }

  return null
}
